import * as React from "react";
import { useIntl } from "react-intl";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { fetchDomains } from "../../../api/strapi/domains";
import { transformDomainsToMegaMenuStructure } from "../../../api/strapi/utils/domains-transformer";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger
} from "../../ui/navigation-menu";

const MegaMenu = () => {
  const intl = useIntl();
  const location = useLocation();
  const [domains, setDomains] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadDomains = async () => {
      try {
        setLoading(true);
        const result = await fetchDomains();
        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Domains unavailable:", result);
            setError("Menu temporarily unavailable");
          }
          setDomains([]);
          return;
        }

        const transformedDomains = transformDomainsToMegaMenuStructure(result.data);
        setDomains(transformedDomains);
      } catch (err) {
        console.error("Erreur lors du chargement des domaines:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDomains();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#16A34A]"></div>
      </div>
    );
  }

  if (error) {
    console.error("Erreur dans le MegaMenu:", error);
    return <div className="p-4 text-gray-500">Menu indisponible</div>;
  }

  // Vérifier si un item d'une catégorie est actif
  const isCategoryActive = (category) => {
    // Utiliser slugFr en priorité car c'est ce qui est utilisé dans les URLs
    const routeSlug = category?.slugFr || category?.slug;
    
    // Vérifier si le pathname commence par le préfixe de la catégorie
    const categoryPrefix = `/info/${routeSlug}/`;
    return location.pathname.startsWith(categoryPrefix);
  };

  return (
    <NavigationMenu className="md:!max-w-full justify-end z-[60]">
      <NavigationMenuList className="flex space-x-2">
        {domains?.map((category) => (
          <NavigationMenuItem key={category.id}>
            <NavigationMenuTrigger className={cn(
              "!px-5 text-[#16A34A] hover:text-[#15803D] data-[state=open]:text-[#15803D] data-[state=open]:bg-transparent font-medium transition-all duration-300 hover:bg-[#16A34A]/10 rounded-md uppercase",
              isCategoryActive(category) ? "bg-[#16A34A]/10 text-[#16A34A] font-semibold" : ""
            )}>
              {category?.title}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="left-0 origin-[var(--radix-navigation-menu-content-transform-origin)] z-[70]">
              {category?.submenus?.length > 0 && (
                <ul className="grid gap-4 p-6 md:grid-cols-2 w-full max-w-[900px] bg-white shadow-lg rounded-md border border-gray-200 font-arial z-[70]">
                  {category?.submenus.map((item, index) => (
                    <li key={item.documentId || index}>
                      <NavigationMenuLink asChild>
                        <div className="flex flex-col rounded-md overflow-hidden">
                          {item.metaImage && (
                            <div className="shrink-0">
                              <img
                                src={`${process.env.REACT_APP_STRAPI_URL}${item.metaImage.url}`}
                                alt={item.title}
                                className="w-full h-32 object-cover"
                              />
                            </div>
                          )}
                          <NavLink
                            to={`/info/${category?.slugFr || category?.slug}/${item?.slug}`}
                            className={({ isActive }) => cn(
                              "block select-none p-4 no-underline outline-none transition-colors hover:bg-gray-50 hover:text-[#16A34A] focus:bg-gray-50 focus:text-[#16A34A] hover:scale-[1.02] transition-transform duration-200",
                              item.metaImage ? "pt-2" : "rounded-md",
                              isActive ? "bg-[#16A34A]/10 text-[#16A34A] font-semibold" : ""
                            )}
                          >
                            <div className="text-[1rem] leading-none text-[#16A34A] font-bold">
                              {item.title}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{item.parentDomain}</p>
                            <hr className="w-10 h-1 bg-[#4CAF50] mt-2" />
                            <p className="leading-snug text-[0.9rem] mt-2 text-gray-600 py-2">
                              {item.metaDescription || item.description}
                            </p>
                          </NavLink>
                        </div>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              )}
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default MegaMenu;
